import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';
import { normalizeSearch } from '@/lib/search';
import { cn } from '@/lib/utils';

import { useCatalogoModulos, useModulosDelPlan, useMutarPlanes } from '../planes.api';
import { ModuleIcon } from '@/features/modulos/components/module-icon';
import type { Plan } from '../planes.types';

interface PlanModulosDialogoProps {
  plan: Plan;
  onClose: () => void;
}

/** Mensaje de error legible para la mutación. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudieron guardar los módulos del plan.';
}

/**
 * Configura los módulos de un plan.
 *
 * Como en el panel anterior, la selección se envía **completa** (reemplaza la
 * anterior) y los módulos se agrupan por categoría con un buscador. El contador
 * muestra cuántos van seleccionados sobre el total del sistema.
 */
export function PlanModulosDialogo({ plan, onClose }: PlanModulosDialogoProps) {
  const catalogo = useCatalogoModulos();
  const asignados = useModulosDelPlan(plan.id);
  const mutaciones = useMutarPlanes();

  const [seleccion, setSeleccion] = useState<Set<number> | null>(null);
  const [buscar, setBuscar] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  // Mientras llegan los módulos del plan se parte de la selección que trae la API.
  const elegidos = useMemo(
    () => seleccion ?? new Set<number>(asignados.data?.modulos ?? []),
    [seleccion, asignados.data],
  );

  const modulos = catalogo.data?.modulos ?? [];
  const categorias = catalogo.data?.categorias ?? [];

  /** Nombre de la categoría (el código del módulo puede venir en otra caja). */
  const nombreCategoria = (codigo: string) =>
    categorias.find((item) => item.codigo.toLowerCase() === codigo.toLowerCase())?.nombre ?? codigo;

  const grupos = useMemo(() => {
    const termino = normalizeSearch(buscar.trim());
    const lista = catalogo.data?.modulos ?? [];
    const mapa = new Map<string, typeof lista>();

    for (const modulo of lista) {
      if (
        termino &&
        !normalizeSearch(`${modulo.nombre} ${modulo.codigo} ${modulo.grupo_permiso ?? ''}`).includes(termino)
      ) {
        continue;
      }

      const clave = modulo.categoria.toLowerCase();
      mapa.set(clave, [...(mapa.get(clave) ?? []), modulo]);
    }

    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalogo.data, buscar]);

  const alternar = (id: number) => {
    const siguiente = new Set(elegidos);

    if (siguiente.has(id)) siguiente.delete(id);
    else siguiente.add(id);

    setSeleccion(siguiente);
  };

  const seleccionarVisibles = (ids: number[], marcar: boolean) => {
    const siguiente = new Set(elegidos);

    for (const id of ids) {
      if (marcar) siguiente.add(id);
      else siguiente.delete(id);
    }

    setSeleccion(siguiente);
  };

  const guardar = () => {
    setAviso(null);

    mutaciones.guardarModulos.mutate(
      { id: plan.id, modulos: [...elegidos] },
      { onSuccess: onClose, onError: (error) => setAviso(mensajeError(error)) },
    );
  };

  const total = modulos.length;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Módulos del plan ${plan.nombre}`}
      description="Marca los módulos que incluye el plan. Se guarda la selección completa."
      size="xl"
      footer={
        <>
          <span className="mr-auto text-sm text-ink-500">
            <strong className="text-ink-900">{elegidos.size}</strong> de {total} módulos seleccionados
          </span>
          <Button variant="outline" onClick={onClose} disabled={mutaciones.guardarModulos.isPending}>
            Cancelar
          </Button>
          <Button onClick={guardar} loading={mutaciones.guardarModulos.isPending}>
            Guardar módulos
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="No se pudieron guardar los módulos">
            {aviso}
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[14rem] flex-1">
            <Input
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar módulo…"
              value={buscar}
              onChange={(event) => setBuscar(event.target.value)}
            />
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => seleccionarVisibles(modulos.map((modulo) => modulo.id), true)}
          >
            Seleccionar todo
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>
            Quitar todo
          </Button>
        </div>

        {catalogo.isPending || asignados.isPending ? (
          <Skeleton className="h-72 rounded-card" />
        ) : catalogo.isError ? (
          <Alert variant="error" title="No se pudo cargar el catálogo de módulos">
            {mensajeError(catalogo.error)}
          </Alert>
        ) : grupos.length === 0 ? (
          <p className="rounded-control border border-dashed border-ink-200 px-4 py-10 text-center text-sm text-ink-500">
            Ningún módulo coincide con la búsqueda.
          </p>
        ) : (
          <div className="max-h-[26rem] space-y-4 overflow-y-auto pr-1">
            {grupos.map(([categoria, lista]) => {
              const ids = lista.map((modulo) => modulo.id);
              const todos = ids.every((id) => elegidos.has(id));

              return (
                <section key={categoria} className="rounded-control border border-ink-100">
                  <header className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 bg-ink-50/60 px-3 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-ink-600">
                      {nombreCategoria(categoria)}
                      <Badge variant="outline" className="ml-2">
                        {lista.length}
                      </Badge>
                    </span>

                    <button
                      type="button"
                      onClick={() => seleccionarVisibles(ids, !todos)}
                      className="text-[11px] font-semibold text-brand-700 hover:underline"
                    >
                      {todos ? 'Quitar todos' : 'Seleccionar todos'}
                    </button>
                  </header>

                  <div className="grid gap-1.5 p-3 sm:grid-cols-2 xl:grid-cols-3">
                    {lista.map((modulo) => {
                      const marcado = elegidos.has(modulo.id);

                      return (
                        <label
                          key={modulo.id}
                          className={cn(
                            'flex cursor-pointer items-start gap-2 rounded-control border px-2.5 py-2 transition-colors',
                            marcado
                              ? 'border-brand-300 bg-brand-50'
                              : 'border-ink-100 bg-white hover:border-brand-200',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => alternar(modulo.id)}
                            className="mt-0.5 size-4 shrink-0 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
                          />

                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <ModuleIcon name={modulo.icono_web} className="size-3.5 shrink-0 text-ink-500" />
                              <span className="truncate text-[13px] font-medium text-ink-900">
                                {modulo.nombre}
                              </span>
                              {!modulo.activo ? (
                                <Badge variant="warning" title="Módulo inactivo">
                                  Inactivo
                                </Badge>
                              ) : null}
                            </span>

                            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-ink-500">
                              {modulo.grupo_permiso ? <span>grupo: {modulo.grupo_permiso}</span> : null}
                              {modulo.sistema === 'LICENCIA' ? <span>licencia</span> : null}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
